/**
 * Comment Service
 * ===============
 * Handles video comment operations.
 */

import { supabase } from '../lib/supabase'

export interface Comment {
  id: string
  video_id: string
  user_id: string
  parent_id: string | null
  content: string
  is_edited: boolean
  is_hidden: boolean
  hidden_reason: string | null
  created_at: string
  updated_at: string
}

export interface CommentWithUser extends Comment {
  user: {
    id: string
    full_name: string | null
    avatar_url: string | null
  }
  replies?: CommentWithUser[]
}

export interface CommentInsert {
  video_id: string
  user_id: string
  parent_id?: string | null
  content: string
}

export interface CommentUpdate {
  content?: string
  is_hidden?: boolean
  hidden_reason?: string | null
}

// Type-safe wrapper for video_comments table operations
// The table exists but types haven't been regenerated yet
const videoCommentsTable = () => supabase.from('video_comments' as 'videos')

export const commentService = {
  /**
   * Get all comments for a video (with user info and nested replies)
   */
  async getVideoComments(videoId: string): Promise<CommentWithUser[]> {
    const { data, error } = await videoCommentsTable()
      .select(`
        *,
        user:users!video_comments_user_id_fkey(id, full_name, avatar_url)
      `)
      .eq('video_id', videoId)
      .eq('is_hidden', false)
      .order('created_at', { ascending: true })

    if (error) {
      console.error('Failed to fetch comments:', error)
      throw error
    }

    // Organize into nested structure
    const comments = (data || []) as unknown as CommentWithUser[]
    return this.nestComments(comments)
  },

  /**
   * Organize flat comments into nested structure
   */
  nestComments(comments: CommentWithUser[]): CommentWithUser[] {
    const commentMap = new Map<string, CommentWithUser>()
    const topLevel: CommentWithUser[] = []

    // First pass: create map
    comments.forEach(comment => {
      commentMap.set(comment.id, { ...comment, replies: [] })
    })

    // Second pass: nest replies
    comments.forEach(comment => {
      const mappedComment = commentMap.get(comment.id)!
      if (comment.parent_id && commentMap.has(comment.parent_id)) {
        commentMap.get(comment.parent_id)!.replies!.push(mappedComment)
      } else {
        topLevel.push(mappedComment)
      }
    })

    return topLevel
  },

  /**
   * Create a new comment
   */
  async createComment(comment: CommentInsert): Promise<CommentWithUser> {
    const { data, error } = await videoCommentsTable()
      .insert(comment as never)
      .select(`
        *,
        user:users!video_comments_user_id_fkey(id, full_name, avatar_url)
      `)
      .single()

    if (error) {
      console.error('Failed to create comment:', error)
      throw error
    }

    return data as unknown as CommentWithUser
  },

  /**
   * Update a comment
   */
  async updateComment(
    commentId: string,
    updates: CommentUpdate
  ): Promise<CommentWithUser> {
    const updateData: CommentUpdate & { is_edited?: boolean } = { ...updates }

    // Mark as edited if content is being updated
    if (updates.content !== undefined) {
      updateData.is_edited = true
    }

    const { data, error } = await videoCommentsTable()
      .update(updateData as never)
      .eq('id', commentId)
      .select(`
        *,
        user:users!video_comments_user_id_fkey(id, full_name, avatar_url)
      `)
      .single()

    if (error) {
      console.error('Failed to update comment:', error)
      throw error
    }

    return data as unknown as CommentWithUser
  },

  /**
   * Delete a comment
   */
  async deleteComment(commentId: string): Promise<boolean> {
    const { error } = await videoCommentsTable()
      .delete()
      .eq('id', commentId)

    if (error) {
      console.error('Failed to delete comment:', error)
      throw error
    }

    return true
  },

  /**
   * Hide a comment (for video owners/moderators)
   */
  async hideComment(
    commentId: string,
    reason?: string
  ): Promise<CommentWithUser> {
    return this.updateComment(commentId, {
      is_hidden: true,
      hidden_reason: reason || 'Hidden by video owner',
    })
  },

  /**
   * Unhide a comment
   */
  async unhideComment(commentId: string): Promise<CommentWithUser> {
    return this.updateComment(commentId, {
      is_hidden: false,
      hidden_reason: null,
    })
  },

  /**
   * Get comment count for a video
   */
  async getCommentCount(videoId: string): Promise<number> {
    const { count, error } = await videoCommentsTable()
      .select('*', { count: 'exact', head: true })
      .eq('video_id', videoId)
      .eq('is_hidden', false)

    if (error) {
      console.error('Failed to get comment count:', error)
      return 0
    }

    return count || 0
  },

  /**
   * Format relative time for comment timestamps
   */
  formatRelativeTime(dateString: string): string {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffSecs = Math.floor(diffMs / 1000)
    const diffMins = Math.floor(diffSecs / 60)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)
    const diffWeeks = Math.floor(diffDays / 7)
    const diffMonths = Math.floor(diffDays / 30)
    const diffYears = Math.floor(diffDays / 365)

    if (diffSecs < 60) return 'just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    if (diffWeeks < 4) return `${diffWeeks}w ago`
    if (diffMonths < 12) return `${diffMonths}mo ago`
    return `${diffYears}y ago`
  },
}
